import {
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    provideZoneChangeDetection,
    APP_INITIALIZER,
} from '@angular/core'
import { provideRouter, withComponentInputBinding } from '@angular/router'
import { provideHttpClient, withInterceptors } from '@angular/common/http'
import { provideAnimations } from '@angular/platform-browser/animations'
import { provideClientHydration, withEventReplay } from '@angular/platform-browser'
import { provideStore, provideState } from '@ngrx/store'
import { provideEffects } from '@ngrx/effects'
import { emailProposalReducer } from './features/oms/store/reducers/email-proposal.reducer'
import { EmailProposalEffects } from './features/oms/store/effects/email-proposal.effects'
import { DatePipe } from '@angular/common'
import {
    CORE_API_CONFIG,
    CORE_ASSET_URL_CONFIG,
    CORE_AUTH_CONFIG,
    CORE_SESSION_CONFIG,
    CORE_PERMISSION_CONFIG,
    CORE_LOGGER_CONFIG,
    CORE_PERFORMANCE_CONFIG,
    CORE_SCREEN_PROTECTION_CONFIG,
} from '@educational-initiatives/angular-web-utilities'
import { GlobalErrorHandler } from '@educational-initiatives/angular-web-utilities'

import { routes } from './app.routes'
import { environment } from 'src/environments/environment'
import { APP_ENVIRONMENT } from './core/tokens/app-environment.token'
import {
    authEndpoints,
    permissionEndpoints,
    IndexDBConstants,
} from './core/constants/valueConstants'
import { AppConstants } from './core/constants/app.constants'
import { SessionService, IndexedDbService } from '@educational-initiatives/angular-web-utilities'
import { PermissionService } from './core/services/permission-service/permission-service'
import { AuthService } from './core/services/auth-service/auth.service'
import { authInterceptor } from './core/interceptors/auth.interceptor'
import { heartbeatInterceptor } from './core/interceptors/heartbeat.interceptor'
import { AppConfigService } from './core/services/app-config.service'
import { provideZard } from '@/styles/core'
import { provideQuillConfig } from 'ngx-quill/config'
import { EMAIL_COMPOSER_QUILL_CONFIG } from '@app/shared/constants/email-composer-dialog.constants'
import {
    Activity,
    BadgeCheck,
    Bell,
    ChevronDown,
    ChevronRight,
    ChevronUp,
    Clock,
    Contact,
    File,
    FileText,
    Headphones,
    HelpCircle,
    Image,
    Info,
    Languages,
    LayoutDashboard,
    List,
    LogIn,
    LogOut,
    LucideAngularModule,
    Menu,
    MoveRight,
    RefreshCw,
    Settings,
    Table,
    User,
    UserPlus,
    Users,
    Pencil,
} from 'lucide-angular'

const svgIconFallbackIcons = {
    Activity,
    BadgeCheck,
    Bell,
    Pencil,
    ChevronDown,
    ChevronRight,
    ChevronUp,
    Clock,
    Contact,
    File,
    FileText,
    Headphones,
    HelpCircle,
    Image,
    Info,
    Languages,
    LayoutDashboard,
    List,
    LogIn,
    LogOut,
    Menu,
    MoveRight,
    RefreshCw,
    Settings,
    Table,
    User,
    UserPlus,
    Users,
}

const subAppId = parseInt(AppConstants.SUB_APP_ID, 10)

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: APP_ENVIRONMENT, useValue: environment },
        { provide: CORE_API_CONFIG, useValue: { baseUrl: environment.apiBaseUrl } },
        {
            provide: CORE_ASSET_URL_CONFIG,
            useValue: {
                assetsBucketUrl: environment.s3Bucket ?? environment.assetsBucketUrl ?? '',
            },
        },
        {
            provide: CORE_AUTH_CONFIG,
            useValue: {
                endpoints: {
                    SUB_APPS: '/api/authX/v1/app/:appId/sub-apps',
                    USER_INFO: authEndpoints.USER_INFO_PATH,
                    LOGOUT: authEndpoints.LOGOUT_PATH,
                    SESSION_ACTIVITY_PATH: authEndpoints.SESSION_ACTIVITY_PATH,
                    SESSION_HEARTBEAT_PATH: authEndpoints.SESSION_HEARTBEAT_PATH,
                },
                defaultAppId: subAppId,
                commonLoginAppId: subAppId,
                includeEnvInfo: true,
                env: environment.name ?? 'development',
            },
        },
        {
            provide: CORE_SESSION_CONFIG,
            useValue: {
                baseUrl: environment.apiBaseUrl,
                sessionActivityPath: authEndpoints.SESSION_ACTIVITY_PATH,
                sessionHeartbeatPath: authEndpoints.SESSION_HEARTBEAT_PATH,
                heartbeatIntervalMs: AppConstants.HEARTBEAT_INTERVAL,
                storageKeys: { SESSION_ACTIVITY: 'x-session-activity-id' },
                subAppId,
            },
        },
        {
            provide: CORE_PERMISSION_CONFIG,
            useValue: {
                baseUrl: environment.apiBaseUrl,
                appId: subAppId,
                indexedDb: {
                    DB: IndexDBConstants.PERMISSIONS_STORE_DB,
                    STORE: IndexDBConstants.PERMISSIONS_STORE_KEY,
                    VERSION: IndexDBConstants.DB_VERSION,
                },
                fetchPermissionsPath: `${permissionEndpoints.GET_PERMISSIONS}/:appId`,
            },
        },
        {
            provide: CORE_LOGGER_CONFIG,
            useValue: {
                enableLog: environment.enableLog ?? false,
                logLevel:
                    (environment.logLevel as 'debug' | 'info' | 'warn' | 'error' | 'fatal') ??
                    'info',
                production: environment.production ?? false,
                logEndpoint: environment.logEndpoint,
                name: environment.name,
                version: environment.version,
            },
        },
        {
            provide: CORE_PERFORMANCE_CONFIG,
            useValue: {
                enablePerformanceMetrics: environment.features?.enablePerformanceMetrics ?? false,
            },
        },
        {
            provide: CORE_SCREEN_PROTECTION_CONFIG,
            useValue: {
                production: environment.production ?? false,
                environmentName: environment.name ?? 'development',
                enableInProduction: true,
                enableInStaging: true,
            },
        },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        provideZoneChangeDetection(),
        provideRouter(routes, withComponentInputBinding()),
        provideHttpClient(withInterceptors([authInterceptor, heartbeatInterceptor])),
        provideClientHydration(withEventReplay()),
        provideAnimations(),
        provideStore(),
        provideState('emailProposal', emailProposalReducer),
        provideEffects([EmailProposalEffects]),
        provideZard(),
        provideQuillConfig(EMAIL_COMPOSER_QUILL_CONFIG),
        DatePipe,
        {
            provide: APP_INITIALIZER,
            useFactory:
                (
                    appConfigService: AppConfigService,
                    indexedDbService: IndexedDbService,
                    sessionService: SessionService,
                    permissionService: PermissionService,
                    authService: AuthService,
                ) =>
                async () => {
                    try {
                        await appConfigService.load()
                        await indexedDbService.open({
                            name: IndexDBConstants.PERMISSIONS_STORE_DB,
                            version: IndexDBConstants.DB_VERSION,
                            stores: [
                                {
                                    name: IndexDBConstants.PERMISSIONS_STORE_KEY,
                                    keyPath: 'id',
                                },
                            ],
                        })
                        await sessionService.createSessionActivity()
                        const permissions = await permissionService.getPermissions()
                        const version = permissions?.['version']
                        if (typeof version === 'number') {
                            await authService.getUserInfo(version)
                        } else {
                            await authService.isAuthenticated()
                        }
                    } catch (error) {
                        console.error('APP_INITIALIZER error:', error)
                    }
                },
            deps: [
                AppConfigService,
                IndexedDbService,
                SessionService,
                PermissionService,
                AuthService,
            ],
            multi: true,
        },
        importProvidersFrom(LucideAngularModule.pick(svgIconFallbackIcons)),
    ],
}
